import { Icon } from "@captions-web/icons";
import { skipToken } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate, Link } from "@tanstack/react-router";
import { Fragment, useRef, useState } from "react";
import { z } from "zod";

import { Button } from "~/components/primitives/button";
import { textInputStyles } from "~/components/primitives/input";
import { Select } from "~/components/primitives/select";
import { getStatsigStore } from "~/lib/analytics/statsig";
import { useOnChangeEffect } from "~/lib/hooks/react-helpers";
import { createLocalStorageDef } from "~/lib/local-storage";
import { getAuthStore } from "~/modules/auth/auth-store";
import { internalLookupInfoQuery } from "~/modules/internal/queries";
import { css, cx, cva } from "~/styled-system/css";

export const Route = createFileRoute("/internal/lookup")({
  validateSearch: z.object({
    type: z.enum(["user", "org"]).optional().default("user"),
    id: z.string().optional(),
  }),
  component: RouteComponent,
  async beforeLoad() {
    const profileResponse = await getAuthStore().ensureProfile();

    if (!profileResponse) {
      throw redirect({ to: "/" });
    }

    const isEmployee = getStatsigStore().getFeatureFlag("isemployee");

    if (!isEmployee) {
      previousSearchesStorage.remove();
      throw redirect({ to: "/" });
    }

    return { user: profileResponse.user };
  },
});

const previousSearchesStorage = createLocalStorageDef(
  "studio_internal_previous_searches",
  z.array(
    z.object({
      type: z.enum(["user", "org"]),
      id: z.string(),
      name: z.string(),
      createdAt: z.string(),
    })
  )
);

function RouteComponent() {
  const { type, id } = Route.useSearch();

  const idInputRef = useRef<HTMLInputElement>(null);
  const [typeValue, setTypeValue] = useState<"user" | "org">(() => type ?? "user");
  const [idValue, setIdValue] = useState<string>(() => id ?? "");

  const [previousSearches, setPreviousSearches] = previousSearchesStorage.use({ defaultValue: [] });

  const navigate = useNavigate({ from: Route.fullPath });

  useOnChangeEffect(id, (v) => {
    setIdValue(v ?? "");
  });

  useOnChangeEffect(type, (v) => {
    setTypeValue(v as "user" | "org");
  });

  const { data, isPending, error } = internalLookupInfoQuery.useQuery(
    type && id ? { params: { type, id } } : skipToken,
    {
      retry: false,
    }
  );

  useOnChangeEffect(data, (data) => {
    if (type && id && data) {
      setPreviousSearches((prev) => {
        const withoutCurrent = (prev ?? []).filter((p) => p.id !== id);

        return [
          {
            type,
            id,
            name: data.user?.name ?? data.orgs[0].orgDetails.name,
            createdAt: new Date().toISOString(),
          },
          ...withoutCurrent,
        ].slice(0, 20);
      });
    }
  });

  const onTypeChange = async (v: string) => {
    await navigate({ search: { type: v as "user" | "org", id: "" } });
    setTimeout(() => {
      // hack to let menu close before focusing
      if (idInputRef.current) {
        idInputRef.current.focus();
      }
    }, 100);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const type = formData.get("type") as "user" | "org";
    const id = formData.get("id") as string;
    navigate({ search: { type, id } });
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    if (pastedText) {
      navigate({ search: { type, id: pastedText } });
    }
  };

  if (error) {
    console.error(error);
  }

  return (
    <main className={mainStyles}>
      <Link
        to="."
        className={css({ textStyle: "heading-3", "&:hover": { textDecoration: "underline" } })}
      >
        Lookup
      </Link>
      <form onSubmit={onSubmit} className={formStyles}>
        <fieldset className={fieldsetStyles}>
          <Select.Root name="type" value={typeValue} onValueChange={onTypeChange}>
            <Select.Trigger className={css({ width: "135px", flexShrink: 0 })} />
            <Select.Content>
              <Select.Item value="user">User</Select.Item>
              <Select.Item value="org">Organization</Select.Item>
            </Select.Content>
          </Select.Root>
          <input
            ref={idInputRef}
            className={cx(textInputStyles, css({ width: "100%" }))}
            placeholder={type === "user" ? "User ID" : "Org ID"}
            name="id"
            value={idValue}
            onChange={(e) => setIdValue(e.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onPaste={onPaste}
            autoComplete="off"
            autoCorrect="off"
            data-1p-ignore
          />
          <Button
            type="submit"
            variant="primary"
            size="md"
            icon={<Icon name="zoom" />}
            isPending={!!id && isPending}
          >
            Lookup
          </Button>
        </fieldset>
      </form>
      <div>
        {!id && (
          <Fragment>
            <p className={css({ textStyle: "body-1", color: "$gray.400" })}>
              Enter a user or org ID to lookup their information.
            </p>
            {previousSearches.length > 0 && (
              <div className={previousSearchesContainerStyles}>
                <h3 className={css({ textStyle: "heading-5" })}>Recent Searches</h3>
                <div>
                  {previousSearches.map((search) => (
                    <Link
                      key={`${search.type}-${search.id}`}
                      from={Route.fullPath}
                      search={{ type: search.type, id: search.id }}
                      className={previousSearchItemStyles}
                    >
                      <span className={searchItemNameStyles}>{search.name}</span>
                      <span className={searchItemMetaStyles}>
                        <span className={badgeStyles({})}>{search.type}</span>
                        {search.id}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </Fragment>
        )}
        {error ? (
          <div className={errorContainerStyles}>
            <Icon name="warning" className={css({ fontSize: "20px" })} />
            <p className={css({ textStyle: "body-1" })}>Failed to load user data</p>
          </div>
        ) : data ? (
          <div className={dataContainerStyles}>
            {data.user && (
              <section className={sectionStyles}>
                <h3 className={sectionTitleStyles}>User Information</h3>
                <div className={infoGridStyles}>
                  <div className={infoItemStyles}>
                    <span className={labelStyles}>Name</span>
                    <span className={valueStyles()}>{data.user.name}</span>
                  </div>
                  <div className={infoItemStyles}>
                    <span className={labelStyles}>Email</span>
                    <span className={valueStyles()}>{data.user.email}</span>
                  </div>
                  <div className={infoItemStyles}>
                    <span className={labelStyles}>User ID</span>
                    <span className={valueStyles()}>{data.user.userId}</span>
                  </div>
                </div>
              </section>
            )}

            {data.orgs.length > 0 && (
              <section className={sectionStyles}>
                <h3 className={sectionTitleStyles}>Organizations</h3>
                {data.orgs.map((org) => (
                  <div key={org.workosOrg.id} className={orgContainerStyles}>
                    <div className={orgHeaderStyles}>
                      <h4 className={css({ textStyle: "heading-5" })}>{org.orgDetails.name}</h4>
                    </div>

                    <div className={orgSectionStyles}>
                      <h5 className={subSectionTitleStyles}>Organization Details</h5>
                      <div className={infoGridStyles}>
                        <div className={infoItemStyles}>
                          <span className={labelStyles}>Org ID</span>
                          <span className={valueStyles()}>
                            <Link
                              from={Route.fullPath}
                              search={{ type: "org", id: org.orgDetails.id }}
                              className={orgIdLinkStyles}
                            >
                              {org.orgDetails.id}
                            </Link>
                          </span>
                        </div>
                        <div className={infoItemStyles}>
                          <span className={labelStyles}>WorkOS ID</span>
                          <span className={valueStyles()}>{org.workosOrg.id}</span>
                        </div>
                      </div>
                    </div>

                    <div className={orgSectionStyles}>
                      <h5 className={subSectionTitleStyles}>Subscription</h5>
                      <div className={infoGridStyles}>
                        <div className={infoItemStyles}>
                          <span className={labelStyles}>Plan</span>
                          <span className={valueStyles()}>
                            {org.orgDetails.subscription?.plan ?? "No plan"}
                          </span>
                        </div>
                        {org.orgDetails.subscription && (
                          <Fragment>
                            <div className={infoItemStyles}>
                              <span className={labelStyles}>Frequency</span>
                              <span className={valueStyles()}>
                                {org.orgDetails.subscription.frequency}
                              </span>
                            </div>
                            <div className={infoItemStyles}>
                              <span className={labelStyles}>Stripe Customer</span>
                              <span className={valueStyles()}>
                                {org.orgDetails.subscription.stripeCustomerId}
                              </span>
                            </div>
                            <div className={infoItemStyles}>
                              <span className={labelStyles}>Stripe Subscription</span>
                              <span className={valueStyles()}>
                                {org.orgDetails.subscription.stripeSubscriptionId}
                              </span>
                            </div>
                            <div className={infoItemStyles}>
                              <span className={labelStyles}>Purchased Seats</span>
                              <span className={valueStyles()}>
                                {org.orgDetails.subscription.seatInfo.purchasedSeats}
                              </span>
                            </div>
                            <div className={infoItemStyles}>
                              <span className={labelStyles}>Current Period</span>
                              <span className={valueStyles()}>
                                {new Date(
                                  org.orgDetails.subscription.currentPeriodStart._seconds * 1000
                                ).toLocaleDateString()}{" "}
                                -{" "}
                                {new Date(
                                  org.orgDetails.subscription.currentPeriodEnd._seconds * 1000
                                ).toLocaleDateString()}
                              </span>
                            </div>
                            {org.orgDetails.subscription.cancelAtPeriodEnd && (
                              <div className={infoItemStyles}>
                                <span className={labelStyles}>Auto-Renewal</span>
                                <span
                                  className={css({
                                    textStyle: "body-1",
                                    color: "$orange.300",
                                    fontWeight: "600",
                                  })}
                                >
                                  Disabled - Will cancel at period end
                                </span>
                              </div>
                            )}
                          </Fragment>
                        )}
                      </div>
                    </div>

                    <div className={orgSectionStyles}>
                      <h5 className={subSectionTitleStyles}>Balance & Credits</h5>
                      <div className={infoGridStyles}>
                        <div className={infoItemStyles}>
                          <span className={labelStyles}>Account Credits</span>
                          <span className={valueStyles()}>
                            {org.orgBalance.credits.account.toLocaleString()}
                          </span>
                        </div>
                        <div className={infoItemStyles}>
                          <span className={labelStyles}>Credit Limit</span>
                          <span className={valueStyles()}>
                            {org.orgBalance.credits.limit.toLocaleString()}
                          </span>
                        </div>
                        <div className={infoItemStyles}>
                          <span className={labelStyles}>Total Credits</span>
                          <span className={valueStyles()}>
                            {(
                              org.orgBalance.credits.account + org.orgBalance.credits.limit
                            ).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className={orgSectionStyles}>
                      <h5 className={subSectionTitleStyles}>Members ({org.orgMembers.length})</h5>
                      <div className={membersGridStyles}>
                        {org.orgMembers.map((member) => (
                          <div key={member.workosUser.id} className={memberCardStyles}>
                            {member.workosUser.profilePictureUrl && (
                              <img
                                src={member.workosUser.profilePictureUrl}
                                alt={member.captionsUser.name}
                                className={avatarStyles}
                              />
                            )}
                            <div className={memberInfoStyles}>
                              <div className={memberNameStyles}>{member.captionsUser.name}</div>
                              <div className={memberEmailStyles}>{member.captionsUser.email}</div>
                              <div className={memberMetaStyles}>
                                <span
                                  className={badgeStyles({
                                    variant: member.role.slug as "admin" | "member",
                                  })}
                                >
                                  {member.role.slug}
                                </span>
                                <Link
                                  from={Route.fullPath}
                                  search={{ type: "user", id: member.captionsUser.userId }}
                                  className={memberIdLinkStyles}
                                >
                                  ID: {member.captionsUser.userId}
                                </Link>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {org.invitations.length > 0 && (
                      <div className={orgSectionStyles}>
                        <h5 className={subSectionTitleStyles}>
                          Invitations ({org.invitations.length})
                        </h5>
                        <div className={invitationsGridStyles}>
                          {org.invitations.map((invitation, index) => (
                            <div
                              key={`${invitation.email}-${index}`}
                              className={invitationCardStyles}
                            >
                              <div className={invitationInfoStyles}>
                                <div className={invitationEmailStyles}>{invitation.email}</div>
                                <div className={invitationMetaStyles}>
                                  <span
                                    className={badgeStyles({
                                      variant: invitation.state as
                                        | "pending"
                                        | "expired"
                                        | "revoked",
                                    })}
                                  >
                                    {invitation.state}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </section>
            )}
          </div>
        ) : null}
      </div>
    </main>
  );
}

const mainStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  width: "100%",
  maxWidth: "900px",
  margin: "40px auto",
  userSelect: "text",
});

const formStyles = css({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  justifyContent: "center",
});

const fieldsetStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "6px",
});

const errorContainerStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  color: "$error",
});

const dataContainerStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "32px",
});

const sectionStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const sectionTitleStyles = css({
  textStyle: "heading-3",
  color: "$gray.100",
  fontWeight: "700",
});

const infoGridStyles = css({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
  gap: "16px",
});

const infoItemStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const labelStyles = css({
  textStyle: "body-2",
  color: "$gray.400",
  fontWeight: "500",
});

const valueStyles = cva({
  base: {
    textStyle: "body-1",
    color: "$gray.50",
  },
  variants: {
    variant: {
      highlight: {
        fontWeight: "600",
        color: "$purple",
      },
    },
  },
});

const orgContainerStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  padding: "8px",
  borderRadius: "12px",
  background: "$gray.800",
});

const orgHeaderStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

const orgSectionStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const orgIdLinkStyles = css({
  "&:hover": {
    color: "white",
    textDecoration: "underline",
  },
});

const subSectionTitleStyles = css({
  textStyle: "heading-6",
  color: "$gray.100",
  fontWeight: "600",
  paddingBottom: "8px",
  borderBottom: "$thin",
});

const membersGridStyles = css({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "8px",
});

const memberCardStyles = css({
  display: "flex",
  gap: "12px",
  padding: "12px",
  borderRadius: "6px",
  boxShadow: "$highlight",
});

const avatarStyles = css({
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  objectFit: "cover",
});

const memberInfoStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flex: 1,
  minWidth: 0,
});

const memberNameStyles = css({
  textStyle: "body-1",
  color: "$gray.50",
  fontWeight: "600",
});

const memberEmailStyles = css({
  textStyle: "body-2",
  color: "$gray.300",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const memberMetaStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "4px",
});

const memberIdLinkStyles = css({
  textStyle: "body-3",
  color: "$gray.300",
  textDecoration: "none",
  transition: "color 0.2s",
  "&:hover": {
    color: "white",
    textDecoration: "underline",
  },
});

const badgeStyles = cva({
  base: {
    display: "inline-flex",
    alignItems: "center",
    padding: "1px 6px",
    borderRadius: "4px",
    textStyle: "body-3",
    fontWeight: "500",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  variants: {
    variant: {
      active: {
        backgroundColor: "$green.950",
        color: "white",
      },
      inactive: {
        backgroundColor: "$gray.900",
        color: "white",
      },
      admin: {
        backgroundColor: "$purple",
        color: "white",
      },
      member: {
        backgroundColor: "$gray.700",
        color: "white",
      },
      user: {
        backgroundColor: "$blue.700",
        color: "white",
      },
      org: {
        backgroundColor: "$purple",
        color: "white",
      },
      pending: {
        backgroundColor: "#FF8C42",
        color: "black",
      },
      expired: {
        backgroundColor: "#FF2D55",
        color: "white",
      },
      revoked: {
        backgroundColor: "#575961",
        color: "white",
      },
    },
  },
  defaultVariants: {
    variant: "member",
  },
});

const previousSearchesContainerStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  marginTop: "32px",
});

const previousSearchItemStyles = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px",
  "&:hover": {
    backgroundColor: "$gray.800",
  },
});

const searchItemNameStyles = css({
  textStyle: "body-1",
  color: "$gray.100",
});

const searchItemMetaStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  textStyle: "body-2",
  color: "$gray.400",
  fontFamily: "monospace",
});

const invitationsGridStyles = css({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "8px",
});

const invitationCardStyles = css({
  display: "flex",
  gap: "12px",
  padding: "12px",
  borderRadius: "6px",
  boxShadow: "$highlight",
});

const invitationInfoStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flex: 1,
  minWidth: 0,
});

const invitationEmailStyles = css({
  textStyle: "body-1",
  color: "$gray.50",
  fontWeight: "600",
});

const invitationMetaStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "4px",
});
